/**
 * Two terminals, one prompt — the hero's instrument.
 *
 * Both panes run the same prompt at the same time. The left is an agent reading
 * files; the right is the same agent with Archis, where every Archis call is an MCP
 * tool call, `chi/<tool>`, with its level and its cost. The right finishes first and
 * waits; both end on the same answer line. The meter under each pane steps by exactly
 * what the last line cost, when it lands. Under them sit the measured session figures
 * from the latest campaign — never a percentage computed from the illustration, which
 * disagreed with the benchmark it sat above.
 *
 * The rules it keeps (docs/DESIGN.md § Visual direction): every line is a quantity;
 * meters step, never roll; no latency figures, because none are measured; a terminal's
 * grammar rather than a vendor's; only public tool names. The prompt is the replay.
 * Reduced motion, and the prerendered page, show the finished transcripts.
 *
 * Replaced the earlier animated "read" diagram in the hero on 2026-09-23: a transcript
 * needs no learning from a reader who watches one all day, and it shows the mechanism
 * in the agent's own terms.
 */

import { useCallback, useState } from 'react'
import { Link } from 'react-router-dom'

import { LATEST_CAMPAIGN } from '@/entities/metrics'
import { capture } from '@/shared/lib/analytics'
import { formatNumber, sleep, usePrefersReducedMotion, useRunner } from '@/shared/ui/motion'
import { DEFAULT_BUDGET, TASKS } from '@/shared/ui/walkthrough'

const BEAT = 400 // ms between lines; the right pane's "done" lands at about 2.5s

type Pane = 'files' | 'archis'
type Phase = 'still' | 'running' | 'done'

const priced = (cost: number | 'budget') => (cost === 'budget' ? DEFAULT_BUDGET : cost)
const { session } = LATEST_CAMPAIGN

/** A signed percentage with a real minus sign. */
const pct = (value: number) => `${value < 0 ? '−' : '+'}${Math.abs(value)}%`

export function TwoTerminals({ initialTask = 0 }: { initialTask?: number } = {}) {
  const reduced = usePrefersReducedMotion()
  const start = useRunner()

  const [taskIndex, setTaskIndex] = useState(initialTask)
  const task = TASKS[taskIndex]!

  // 'still' is the prerendered page and reduced motion: both transcripts complete.
  const [phase, setPhase] = useState<Phase>('still')
  const [shownA, setShownA] = useState(task.base.length)
  const [shownB, setShownB] = useState(task.calls.length)
  const [doneA, setDoneA] = useState(true)
  const [doneB, setDoneB] = useState(true)
  const [pane, setPane] = useState<Pane>('archis') // the pane a phone shows

  const totalA = task.base.slice(0, shownA).reduce((n, [, cost]) => n + cost, 0)
  const totalB = task.calls.slice(0, shownB).reduce((n, [, , , , cost]) => n + priced(cost), 0)

  const run = useCallback(
    async (index: number) => {
      const token = start()
      const t = TASKS[index]!
      if (reduced) {
        setShownA(t.base.length)
        setShownB(t.calls.length)
        setDoneA(true)
        setDoneB(true)
        setPhase('done')
        return
      }
      setPhase('running')
      setShownA(0)
      setShownB(0)
      setDoneA(false)
      setDoneB(false)
      await sleep(350)
      const rows = Math.max(t.base.length, t.calls.length)
      for (let i = 1; i <= rows + 1; i += 1) {
        if (!token.live) return
        if (i <= t.base.length) setShownA(i)
        else if (i === t.base.length + 1) setDoneA(true)
        if (i <= t.calls.length) setShownB(i)
        else if (i === t.calls.length + 1) setDoneB(true)
        await sleep(BEAT)
      }
      if (!token.live) return
      setDoneA(true)
      setDoneB(true)
      setPhase('done')
    },
    [start, reduced],
  )

  // No run on load. The prerendered page shows both transcripts finished, and that is
  // what a reader, a thumbnail and a link preview should see; replaying on load wiped
  // it and left the page empty for seconds. The transcript replays when the reader
  // picks a task or clicks the prompt.

  const choose = (index: number) => {
    setTaskIndex(index)
    capture('landing_terminals_task', { task: TASKS[index]!.short })
    void run(index)
  }
  const replay = () => {
    capture('landing_terminals_replayed')
    void run(taskIndex)
  }

  return (
    <div className="tt" data-phase={phase} data-pane={pane}>
      <div className="tt__tabs" role="tablist" aria-label="The task">
        {TASKS.map((entry, index) => (
          <button
            key={entry.label}
            type="button"
            role="tab"
            aria-selected={index === taskIndex}
            className="tt__tab"
            onClick={() => choose(index)}
          >
            {entry.short}
          </button>
        ))}
      </div>

      {/* On a phone there is room for one terminal; both meters stay in view. */}
      <div className="tt__switch" role="tablist" aria-label="Which agent">
        <button type="button" role="tab" aria-selected={pane === 'files'} onClick={() => setPane('files')}>
          Dump whole files <b>{formatNumber(totalA)}</b>
        </button>
        <button type="button" role="tab" aria-selected={pane === 'archis'} onClick={() => setPane('archis')}>
          With Archis <b className="signal">{formatNumber(totalB)}</b>
        </button>
      </div>

      <div className="tt__panes">
        <div className="term tt__term tt__term--files">
          <div className="term__bar">
            <span className="tt__dot" />
            <b>agent</b>&nbsp;· dump whole files
          </div>
          <div className="tt__body">
            <Prompt label={task.label} onReplay={replay} />
            {task.base.slice(0, shownA).map(([what, cost]) => {
              const [verb, ...rest] = what.split(' ')
              return (
                <div className="tt__line tt__line--call" key={what}>
                  <span className="tt__g">▸</span>
                  <span className="tt__what">
                    {verb} <span className="tt__arg">{rest.join(' ')}</span>
                  </span>
                  <span className="tt__cost">{formatNumber(cost)}</span>
                </div>
              )
            })}
            {doneA && <Answer text={task.answer} />}
          </div>
          <Meter label="context read" value={totalA} />
        </div>

        <div className="term tt__term tt__term--archis">
          <div className="term__bar">
            <span className="tt__dot" />
            <b>agent</b>&nbsp;· with Archis
          </div>
          <div className="tt__body">
            <Prompt label={task.label} onReplay={replay} />
            {task.calls.slice(0, shownB).map(([level, tool, arg, result, cost]) => (
              <div key={tool + arg}>
                <div className="tt__line tt__line--call">
                  <span className="tt__g">▸</span>
                  <span className="tt__what">
                    <span className="tt__lv">{level}</span>chi/{tool}{' '}
                    <span className="tt__arg">{cost === 'budget' ? `budget ${formatNumber(DEFAULT_BUDGET)}` : arg}</span>
                  </span>
                  <span className="tt__cost">{formatNumber(priced(cost))}</span>
                </div>
                <div className="tt__line tt__line--result">
                  <span className="tt__g">↳</span>
                  <span>{result}</span>
                  <span />
                </div>
              </div>
            ))}
            {doneB && <Answer text={task.answer} />}
            {doneB && !doneA && (
              <div className="tt__line tt__line--wait">
                <span />
                <span>done · waiting for the other agent</span>
                <span />
              </div>
            )}
          </div>
          <Meter label="context read" value={totalB} signal />
        </div>
      </div>

      {/* The payoff is measured, not illustrated: the latest campaign's session figures,
          so the boxes cannot disagree with the benchmark table further down the page.
          The transcripts above are the mechanism, with round numbers per step. */}
      <div className="tt__result">
        <div className="tt__box">
          <span className="tt__fig tt__fig--signal">{pct(session.medianPerPair)}</span>
          <span>tokens in a typical session</span>
        </div>
        <div className="tt__box">
          <span className="tt__fig">{pct(session.bothSolved)}</span>
          <span>on tasks both agents solved</span>
        </div>
        <div className="tt__box">
          <span className="tt__fig">0</span>
          <span>lines of your code uploaded</span>
        </div>
      </div>
      <p className="tt__note">
        Transcripts illustrative. Figures measured: {session.agent}, {session.pairs} paired sessions, Archis{' '}
        {session.build}, early. <Link to="/verify#benchmark">How we measured</Link>
      </p>
    </div>
  )
}

function Prompt({ label, onReplay }: { label: string; onReplay: () => void }) {
  return (
    <div className="tt__line tt__line--prompt">
      <span className="tt__g">›</span>
      <button className="tt__prompt" type="button" onClick={onReplay} aria-label="Run this prompt again">
        {label}
      </button>
      <span />
    </div>
  )
}

function Answer({ text }: { text: string }) {
  return (
    <div className="tt__line tt__line--answer">
      <span className="tt__g">✓</span>
      <span>{text}</span>
      <span />
    </div>
  )
}

/** A meter, not a slot machine: it shows only sums that were true. The key restarts the tick. */
function Meter({ label, value, signal = false }: { label: string; value: number; signal?: boolean }) {
  return (
    <div className="tt__foot">
      <span>{label}</span>
      <span className={`tt__total${signal ? ' tt__total--signal' : ''}`} key={value}>
        {formatNumber(value)} tok
      </span>
    </div>
  )
}
